<template>
  <div class="financing-month-picker" :class="{ 'is-compact': compact }" v-click-outside="close">
    <button
      type="button"
      class="picker-trigger"
      :disabled="disabled"
      :class="{ open: open, compact }"
      @click="toggle"
    >
      <i class="las la-calendar"></i>
      <span>{{ displayLabel }}</span>
      <i class="las la-angle-down chevron" :class="{ rotated: open }"></i>
    </button>
    <div v-if="open" class="picker-popover" :class="{ compact }">
      <div class="year-row">
        <button type="button" class="nav-btn" @click="shiftYear(-1)">
          <i class="las la-angle-left"></i>
        </button>
        <span class="year-label">{{ viewYear }}</span>
        <button type="button" class="nav-btn" @click="shiftYear(1)">
          <i class="las la-angle-right"></i>
        </button>
      </div>
      <div class="month-grid">
        <button
          v-for="(month, index) in months"
          :key="month"
          type="button"
          class="month-btn"
          :class="{ selected: isSelected(index) }"
          @click="selectMonth(index)"
        >
          {{ month }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default {
  name: "FinancingMonthPicker",
  props: {
    value: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "Select month",
    },
  },
  data() {
    const parsed = this.parseValue(this.value);
    return {
      open: false,
      months: MONTHS,
      viewYear: parsed.year,
    };
  },
  computed: {
    selectedYear() {
      return this.parseValue(this.value).year;
    },
    selectedMonth() {
      return this.parseValue(this.value).month;
    },
    displayLabel() {
      if (this.selectedMonth == null) return this.placeholder;
      return `${MONTHS_LONG[this.selectedMonth]} ${this.selectedYear}`;
    },
  },
  watch: {
    value(next) {
      this.viewYear = this.parseValue(next).year;
    },
  },
  methods: {
    parseValue(value) {
      const now = new Date();
      const fallbackYear = now.getFullYear();
      if (!value || !/^\d{4}-\d{2}$/.test(value)) {
        return { year: fallbackYear, month: null };
      }
      const [year, month] = value.split("-").map(Number);
      return { year, month: month - 1 };
    },
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
      if (this.open) {
        this.viewYear = this.selectedYear;
      }
    },
    close() {
      this.open = false;
    },
    shiftYear(delta) {
      this.viewYear += delta;
    },
    isSelected(index) {
      return this.viewYear === this.selectedYear && index === this.selectedMonth;
    },
    selectMonth(index) {
      const period = `${this.viewYear}-${String(index + 1).padStart(2, "0")}`;
      this.$emit("input", period);
      this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.financing-month-picker {
  position: relative;
  display: inline-flex;

  &:not(.is-compact) {
    display: flex;
    width: 100%;
  }
}

.picker-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  min-width: 190px;
  padding: 0 0.75rem;
  background: #fff;
  border: 1px solid #dbe3ef;
  border-radius: 0.5rem;
  color: #212529;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  width: 100%;

  i.la-calendar {
    color: #2774E9;
    font-size: 1.05rem;
  }

  span {
    flex: 1;
    text-align: left;
  }

  .chevron {
    color: #6c757d;
    font-size: 0.85rem;
    transition: transform 0.15s ease;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  &:hover:not(:disabled),
  &.open {
    border-color: #2774E9;
    box-shadow: 0 0 0 3px rgba(39, 116, 233, 0.12);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &.compact {
    height: 34px;
    min-width: 176px;
    font-size: 0.8125rem;
    border-radius: 0.375rem;
  }
}

.picker-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 40;
  width: 248px;
  background: #fff;
  border: 1px solid #e7edf5;
  border-radius: 0.75rem;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
  padding: 0.85rem 0.75rem 0.9rem;

  &:not(.compact) {
    left: 0;
    right: auto;
  }
}

.year-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.year-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #212529;
}

.nav-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 0.375rem;
  background: #f4f7fb;
  color: #2774E9;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e8f1fd;
  }
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.4rem;
}

.month-btn {
  height: 34px;
  border: none;
  border-radius: 0.4rem;
  background: #f8fafc;
  color: #334155;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e8f1fd;
    color: #2774E9;
  }

  &.selected {
    background: #2774E9;
    color: #fff;
  }
}
</style>
